import React, { useState, useEffect } from 'react';
import { websocketService } from '../services/websocketService';
import { Button } from './ui/button';
import { CardKey, EditQuestionRequest, editCardFields, isResponseToEdit } from '../utils/cardAnswerPairing';

interface QuestionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalQuestion: string;
  /** The edited card's key: sent with the request so the reply (and the saved row) can name the card. */
  cardKey?: CardKey;
  /** The edited card's own timestamp (its raising line's), sent as the request's `timestamp`. */
  cardTimestamp: string;
  onQuestionEdited: (originalQuestion: string, editedQuestion: string, answer: string) => void;
}

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({
  isOpen,
  onClose,
  originalQuestion,
  cardKey,
  cardTimestamp,
  onQuestionEdited,
}) => {
  const [editedQuestion, setEditedQuestion] = useState(originalQuestion);
  const [isGenerating, setIsGenerating] = useState(false);
  const [answer, setAnswer] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Reset state when modal opens with new question
  useEffect(() => {
    if (isOpen) {
      setEditedQuestion(originalQuestion);
      setAnswer('');
      setError('');
      setIsGenerating(false);
    }
  }, [isOpen, originalQuestion]);

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedQuestion(e.target.value);
    setAnswer('');
    setError('');
  };

  const generateAnswer = async () => {
    if (!editedQuestion.trim()) {
      setError('Please enter a question');
      return;
    }

    setIsGenerating(true);
    setError('');
    setAnswer('');

    // This request, as its reply will be matched against (isResponseToEdit).
    const request: EditQuestionRequest = { originalQuestion, editedQuestion, cardKey: cardKey ?? null };

    try {
      // Send edited question to backend via WebSocket
      websocketService.sendMessage({
        type: 'edit_question',
        data: {
          original_question: originalQuestion,
          edited_question: editedQuestion,
          ...editCardFields(request.cardKey, cardTimestamp),
        },
      });

      // Set up a timeout for the response
      const timeout = setTimeout(() => {
        setIsGenerating(false);
        setError('Request timed out. Please try again.');
      }, 30000); // 30 second timeout

      // Listen for the response using custom event
      const handleEditResponse = (event: CustomEvent) => {
        const responseData = event.detail;
        if (isResponseToEdit(responseData, request)) {
          clearTimeout(timeout);
          setIsGenerating(false);
          
          if (responseData.error) {
            setError(responseData.error);
          } else {
            setAnswer(responseData.answer);
          }
          
          // Remove the event listener
          window.removeEventListener('editQuestionResponse', handleEditResponse as EventListener);
        }
      };

      // Add event listener for the response
      window.addEventListener('editQuestionResponse', handleEditResponse as EventListener);

    } catch (error) {
      setIsGenerating(false);
      setError('Failed to send request. Please check your connection.');
    }
  };

  const handleSave = () => {
    if (answer) {
      onQuestionEdited(originalQuestion, editedQuestion, answer);
      onClose();
    }
  };

  const formatAnswer = (answerText: string) => {
    const lines = answerText.split('\n');
    const formattedLines = lines.map((line, index) => {
      if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
        return (
          <li key={index} className="ml-3 mb-1 text-sm">
            {line.trim().substring(1).trim()}
          </li>
        );
      }
      if (line.trim()) {
        return (
          <p key={index} className="mb-2 text-sm">
            {line.trim()}
          </p>
        );
      }
      return null;
    }).filter(Boolean);

    return <div>{formattedLines}</div>;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-panel max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-surface-subtle px-6 py-4 border-b border-hairline">
          <div className="flex items-center justify-between">
            <h2 className="heading-4 flex items-center">
              Edit Question
            </h2>
            <Button variant="tertiary" onClick={onClose}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Original Question */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Original Question:
            </label>
            <div className="bg-surface-subtle rounded-lg p-3 text-sm text-ink-secondary">
              {originalQuestion}
            </div>
          </div>

          {/* Edited Question */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Edit Question:
            </label>
            <textarea
              value={editedQuestion}
              onChange={handleQuestionChange}
              className="w-full h-24 p-3 border border-ink-border rounded-lg resize-none focus:ring-2 focus:ring-signature focus:border-transparent"
              placeholder="Edit the question here..."
            />
          </div>

          {/* Generate Answer Button */}
          <div className="flex justify-center">
            <Button
              variant="primary"
              onClick={generateAnswer}
              disabled={isGenerating || !editedQuestion.trim()}
              className="flex items-center"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating Answer...
                </>
              ) : (
                <>
                  Generate Answer
                </>
              )}
            </Button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error-tint border border-error rounded-lg p-3">
              <div className="flex items-center text-error">
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Generated Answer */}
          {answer && (
            <div className="bg-success-tint border border-success rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-success flex items-center">
                  Generated Answer:
                </h3>
                <div className="text-xs text-success">
                  Real-time AI response
                </div>
              </div>
              <div className="text-ink prose prose-sm max-w-none">
                {formatAnswer(answer)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-surface-subtle px-6 py-4 border-t border-hairline flex justify-end space-x-3">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!answer}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditModal; 